import { useEffect, useRef, useState } from 'react'

import { api, ApiError } from '../../api/client'
import type { Media } from '../../api/types'
import { XIcon } from '../../components/Icons'

/**
 * Ein GIF zu einer Antwort — gesucht bei Giphy, geholt vom Server.
 *
 * **Warum die Vorschläge oben stehen und das Suchfeld darunter.** Wer eine
 * Kachel antippt, hat nichts geschrieben, was hinausgeht: der Begriff stand
 * vorher fest. Getippt wird nur, wenn die acht nicht reichen — und genau das
 * ist der Satz, der in den Datenschutzhinweisen steht.
 *
 * Die Vorschaubildchen kommen unmittelbar von Giphy in diesen Browser; das
 * gewählte GIF holt danach der Server und legt es ab wie einen Upload.
 * Angezeigt wird es fortan aus der eigenen Anwendung.
 */
export interface GifTreffer {
  id: string
  titel: string
  vorschau: string
  breite: number
  hoehe: number
  bytes: number
}

interface Antwort {
  aktiv: boolean
  stimmungen: string[]
  treffer: GifTreffer[]
}

/**
 * Ob der Dienst überhaupt eingerichtet ist.
 *
 * Einmal gefragt und dann gemerkt: ohne Schlüssel steht der Knopf gar nicht
 * erst da, und die Anwendung ist vollständig benutzbar — dieselbe Regel wie
 * bei Ort und Wetter.
 */
let bekannt: boolean | null = null

export function useGifsAktiv(): boolean {
  const [aktiv, setAktiv] = useState(bekannt ?? false)

  useEffect(() => {
    if (bekannt !== null) return
    void api
      .get<Antwort>('/api/gifs')
      .then((antwort) => {
        bekannt = antwort.aktiv
        setAktiv(antwort.aktiv)
      })
      // Ein Fehler heißt hier: kein Knopf. Nicht mehr und nicht weniger.
      .catch(() => {
        bekannt = false
      })
  }, [])

  return aktiv
}

export function Gifwahl({
  tripId,
  onGewaehlt,
  onZu,
}: {
  tripId: number
  /** Das fertige Medium — es hängt danach an der Nachricht wie ein Upload. */
  onGewaehlt: (media: Media) => void
  onZu: () => void
}) {
  const [begriff, setBegriff] = useState('')
  const [stimmungen, setStimmungen] = useState<string[]>([])
  const [treffer, setTreffer] = useState<GifTreffer[]>([])
  const [laedt, setLaedt] = useState(true)
  const [holt, setHolt] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  // Was zuletzt gesucht wurde -- eine spät eintreffende Antwort auf einen
  // längst überholten Begriff darf die Liste nicht mehr überschreiben.
  const lauf = useRef(0)

  useEffect(() => {
    const meiner = ++lauf.current
    setLaedt(true)
    // Nicht bei jedem Tastendruck: das ginge Zeichen für Zeichen hinaus.
    const id = setTimeout(() => {
      void api
        .get<Antwort>('/api/gifs', begriff.trim() === '' ? undefined : { q: begriff.trim() })
        .then((antwort) => {
          if (meiner !== lauf.current) return
          setStimmungen(antwort.stimmungen)
          setTreffer(antwort.treffer)
          setProblem(antwort.treffer.length === 0 ? 'Dazu gibt es gerade nichts.' : null)
        })
        .catch((fehler) => {
          if (meiner !== lauf.current) return
          setTreffer([])
          setProblem(
            fehler instanceof ApiError && fehler.offline
              ? 'Ohne Netz gibt es keine GIFs.'
              : 'Die Suche hat gerade nicht geklappt.',
          )
        })
        .finally(() => {
          if (meiner === lauf.current) setLaedt(false)
        })
    }, begriff.trim() === '' ? 0 : 350)

    return () => clearTimeout(id)
  }, [begriff])

  async function waehle(gif: GifTreffer) {
    if (holt !== null) return
    setHolt(gif.id)
    setProblem(null)
    try {
      // Der Server holt es und legt es ab. Was zurückkommt, ist ein Medium
      // dieser Reise -- ab hier gibt es keinen Unterschied mehr zu einem
      // hochgeladenen Bild.
      const media = await api.post<Media>(`/api/trips/${tripId}/media/gif`, { id: gif.id })
      onGewaehlt(media)
      onZu()
    } catch (fehler) {
      setProblem(
        fehler instanceof Error ? fehler.message : 'Das GIF ließ sich nicht holen.',
      )
    } finally {
      setHolt(null)
    }
  }

  return (
    <div className="gifwahl" role="dialog" aria-label="GIF auswählen">
      <div className="gifwahl__kopf">
        <input
          className="gifwahl__feld"
          type="search"
          value={begriff}
          placeholder="GIFs suchen …"
          aria-label="GIFs suchen"
          enterKeyHint="search"
          onChange={(event) => setBegriff(event.target.value)}
        />
        <button
          type="button"
          className="icon-button"
          onClick={onZu}
          aria-label="Auswahl schließen"
          title="Schließen"
        >
          <XIcon size={16} />
        </button>
      </div>

      {/* Die Schubladen zuerst. Wer hier tippt, hat nichts hinausgeschickt,
          was er selbst formuliert hat. */}
      {begriff.trim() === '' && stimmungen.length > 0 && (
        <div className="gifwahl__stimmungen">
          {stimmungen.map((wort) => (
            <button
              key={wort}
              type="button"
              className="gifwahl__stimmung"
              onClick={() => setBegriff(wort)}
            >
              {wort}
            </button>
          ))}
        </div>
      )}

      <div className="gifwahl__gitter">
        {treffer.map((gif) => (
          <button
            key={gif.id}
            type="button"
            className={holt === gif.id ? 'gifwahl__kachel gifwahl__kachel--holt' : 'gifwahl__kachel'}
            disabled={holt !== null}
            onClick={() => void waehle(gif)}
            title={gif.titel}
          >
            {/* `loading="lazy"`: die Bildchen kommen von Giphy, und was nicht
                im Bild ist, muss auch nicht geholt werden. */}
            <img src={gif.vorschau} alt={gif.titel} loading="lazy" />
          </button>
        ))}
      </div>

      {(laedt || problem) && (
        <p className="gifwahl__status" role="status">
          {laedt ? 'Suche …' : problem}
        </p>
      )}

      {/* **Die Nennung ist Pflicht**, wie beim Lizenzvermerk der Karte: wer
          einen fremden Dienst benutzt, sagt es dort, wo er benutzt wird. */}
      <p className="gifwahl__quelle">
        GIFs von <strong>GIPHY</strong>. Der Suchbegriff geht dorthin, deine
        Adresse nicht.
      </p>
    </div>
  )
}
